import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import * as XLSX from "xlsx";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import { Label } from "./ui/label";
import { DataTable, DataTableColumn } from "./ui/data-table";
import { DateRangePicker } from "./DateRangePicker";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale("ko");

interface DetailModalContentProps {
  modalData: any;
}

const DetailModalContent: React.FC<DetailModalContentProps> = ({
  modalData,
}) => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    null
  );
  const [selectedMenu, setSelectedMenu] = useState<string>("전체");
  const [selectedFloor, setSelectedFloor] = useState<string>("전체");
  const [dateOrder, setDateOrder] = useState<"asc" | "desc">("asc");

  const rows: any[] = Array.isArray(modalData?.rows) ? modalData.rows : [];

  useEffect(() => {
    setDateRange(null);
    setSelectedMenu("전체");
    setSelectedFloor("전체");
    setDateOrder("asc");
  }, [modalData]);

  const menuList = useMemo(
    () => Array.from(new Set(rows.map((r) => r.productName))).filter(Boolean),
    [rows]
  );
  const floorList = useMemo(
    () => Array.from(new Set(rows.map((r) => r.posAt))).filter(Boolean),
    [rows]
  );

  const filteredRows = useMemo(() => {
    return rows.filter((row: any) => {
      let pass = true;

      if (dateRange?.[0] && dateRange?.[1]) {
        const rd = dayjs(row.saleDate);
        pass =
          pass &&
          rd.isSameOrAfter(dateRange[0], "day") &&
          rd.isSameOrBefore(dateRange[1], "day");
      }

      if (selectedMenu !== "전체") {
        pass = pass && row.productName === selectedMenu;
      }

      if (selectedFloor !== "전체") {
        pass = pass && row.posAt === selectedFloor;
      }

      return pass;
    });
  }, [rows, dateRange, selectedMenu, selectedFloor]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a: any, b: any) => {
      const ta = dayjs(a.saleDate).valueOf() || 0;
      const tb = dayjs(b.saleDate).valueOf() || 0;
      return dateOrder === "asc" ? ta - tb : tb - ta;
    });
    return arr;
  }, [filteredRows, dateOrder]);

  const handleExportExcel = () => {
    if (sortedRows.length === 0) {
      toast.warning("다운로드할 데이터가 없습니다.");
      return;
    }

    const exportRows = sortedRows.map((r: any) => ({
      매출일시: r.saleDate ? dayjs(r.saleDate).format("YYYY-MM-DD HH:mm") : "",
      메뉴이름: r.productName ?? "",
      단가: r.unitPrice ?? "",
      수량: r.quantity ?? "",
      총판매가: r.totalPrice ?? "",
      고객명: r.customerName ?? "",
      층: r.posAt ?? "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "데이터");

    const rawTitle = (modalData?.title ?? "").trim();
    const safeTitle = (rawTitle === "" ? "무제목" : rawTitle).replace(
      /[\\/:*?"<>|]/g,
      "_"
    );

    const fname = `상세목록_${safeTitle}.xlsx`;
    XLSX.writeFile(wb, fname);
  };

  const columns: DataTableColumn<any>[] = [
    {
      title: "매출일시",
      dataIndex: "saleDate",
      render: (v: any) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-"),
    },
    { title: "메뉴 이름", dataIndex: "productName" },
    { title: "단가", dataIndex: "unitPrice" },
    { title: "수량", dataIndex: "quantity" },
    { title: "총 판매가", dataIndex: "totalPrice" },
    { title: "고객명", dataIndex: "customerName" },
    { title: "층", dataIndex: "posAt" },
  ];

  return (
    <div className="flex min-h-[70vh] flex-col">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label>기간</Label>
            <DateRangePicker
              value={dateRange}
              onChange={(next) => setDateRange(next)}
              placeholder="기간 선택"
            />
          </div>

          <div className="space-y-1">
            <Label>메뉴</Label>
            <Select value={selectedMenu} onValueChange={setSelectedMenu}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="전체 메뉴" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="전체">전체 메뉴</SelectItem>
                {menuList.map((menu) => (
                  <SelectItem key={String(menu)} value={String(menu)}>
                    {String(menu)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>층</Label>
            <Select value={selectedFloor} onValueChange={setSelectedFloor}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="전체 층" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="전체">전체 층</SelectItem>
                {floorList.map((floor) => (
                  <SelectItem key={String(floor)} value={String(floor)}>
                    {String(floor)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>정렬</Label>
            <ToggleGroup
              type="single"
              value={dateOrder}
              onValueChange={(v) => v && setDateOrder(v as "asc" | "desc")}
            >
              <ToggleGroupItem value="asc">
                오름차순 (오래된→최신)
              </ToggleGroupItem>
              <ToggleGroupItem value="desc">
                내림차순 (최신→오래된)
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            총 {sortedRows.length}건
          </span>
          <Button variant="outline" onClick={handleExportExcel}>
            <Download className="mr-2 h-4 w-4" />
            엑셀 다운로드
          </Button>
        </div>
      </div>

      <div className="mt-4 flex-1">
        <DataTable
          dataSource={sortedRows}
          columns={columns}
          pagination={false}
          search={{
            keys: ["productName", "customerName", "posAt"],
            placeholder: "메뉴/고객명/층 검색",
          }}
        />
      </div>
    </div>
  );
};

export default DetailModalContent;
